import React, { useState, useEffect } from "react";
import {
    Plus,
    Search,
    Trash2,
    Edit2,
    Newspaper,
    Quote as QuoteIcon,
    Loader2,
    Maximize2,
    Power
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PressService } from "../../services/admin/PressService";
import { useNotification } from "../../context/NotificationContext";
import ConfirmationModal from "../../components/admin/ConfirmationModal";
import QuickViewModal from "../../components/press/QuickViewModal";

export default function PressPage() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [activeTab, setActiveTab] = useState("coverage");
    const [pressList, setPressList] = useState([]);
    const [quotesList, setQuotesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Delete Modal State
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        type: null, // "press" or "quote"
        id: null,
        title: ""
    });

    // Quick View Modal State
    const [previewModal, setPreviewModal] = useState({
        isOpen: false,
        item: null,
        type: null
    });



    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [pressData, quotesData] = await Promise.all([
                    PressService.getAllPress(),
                    PressService.getAllQuotes()
                ]);
                setPressList(pressData);
                setQuotesList(quotesData);
            } catch (error) {
                showNotification({ message: "Failed to fetch press data", type: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [showNotification]);

    const togglePressStatus = async (item) => {
        try {
            const newStatus = item.status === "active" ? "disabled" : "active";
            await PressService.updatePress(item.id, { status: newStatus });
            setPressList(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
            showNotification({ message: `Press item ${newStatus === "active" ? "enabled" : "disabled"}`, type: "success" });
        } catch (error) {
            showNotification({ message: "Failed to update status", type: "error" });
        }
    };

    const toggleQuoteStatus = async (item) => {
        try {
            const newStatus = item.status === "active" ? "disabled" : "active";
            await PressService.updateQuote(item.id, { status: newStatus });
            setQuotesList(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
            showNotification({ message: `Quote ${newStatus === "active" ? "enabled" : "disabled"}`, type: "success" });
        } catch (error) {
            showNotification({ message: "Failed to update status", type: "error" });
        }
    };

    const openQuickView = (type, item) => {
        setPreviewModal({
            isOpen: true,
            item,
            type
        });
    };

    const handleQuickViewEdit = () => {
        const { type, item } = previewModal;
        setPreviewModal({ isOpen: false, item: null, type: null });
        if (type === "press") {
            navigate(`/admin/press/edit/${item.id}`);
        } else {
            navigate(`/admin/press/quotes/edit/${item.id}`);
        }
    };

    const openDeleteModal = (type, item) => {
        setDeleteModal({
            isOpen: true,
            type,
            id: item.id,
            title: type === "press" ? item.headline : "this quote"
        });
    };

    const handleDelete = async () => {
        const { type, id } = deleteModal;
        try {
            if (type === "press") {
                await PressService.deletePress(id);
                setPressList(prev => prev.filter(i => i.id !== id));
            } else {
                await PressService.deleteQuote(id);
                setQuotesList(prev => prev.filter(i => i.id !== id));
            }
            showNotification({ message: `${type === "press" ? "Press item" : "Quote"} deleted successfully`, type: "success" });
        } catch (error) {
            showNotification({ message: `Failed to delete ${type}`, type: "error" });
        } finally {
            setDeleteModal({ isOpen: false, type: null, id: null, title: "" });
        }
    };

    const filteredPress = (Array.isArray(pressList) ? pressList : []).filter(item =>
        (item.publication || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
        (item.headline || '').toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    const filteredQuotes = (Array.isArray(quotesList) ? quotesList : []).filter(item =>
        (item.text || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
        (item.source || '').toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    return (
        <>
            <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Press & Media
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            Manage publication coverage and featured quotes seen on your public site.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate(activeTab === "coverage" ? "/admin/press/add" : "/admin/press/quotes/add")}
                        className="flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all w-full md:w-auto shadow-lg shadow-black/10"
                    >
                        <Plus size={18} /> {activeTab === "coverage" ? "ADD COVERAGE" : "ADD QUOTE"}
                    </button>
                </div>

                {/* Navigation Tabs */}
                <div className="flex border-b border-black/5 dark:border-white/5 mb-8">
                    <button
                        onClick={() => setActiveTab("coverage")}
                        className={`px-8 py-4 text-xs font-bold tracking-widest transition-all relative ${activeTab === "coverage" ? "text-jazz-brown" : "text-black dark:text-white/50 hover:text-black dark:hover:text-white"}`}
                    >
                        COVERAGE LIST
                        {activeTab === "coverage" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-jazz-brown" />}
                    </button>
                    <button
                        onClick={() => setActiveTab("quotes")}
                        className={`px-8 py-4 text-xs font-bold tracking-widest transition-all relative ${activeTab === "quotes" ? "text-jazz-brown" : "text-black dark:text-white/50 hover:text-black dark:hover:text-white"}`}
                    >
                        FEATURED QUOTES
                        {activeTab === "quotes" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-jazz-brown" />}
                    </button>
                </div>

                {/* Filters & Search */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm mb-8">
                    <div className="relative w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60 dark:text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder={activeTab === "coverage" ? "Search publications, headlines..." : "Search quotes, sources..."}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm text-black dark:text-white placeholder-black/70 dark:placeholder-white/40"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 size={40} className="text-jazz-brown animate-spin mb-4" />
                        <p className="text-black dark:text-white/50 text-xs font-bold tracking-widest uppercase">Loading content...</p>
                    </div>
                ) : (
                    <>
                        {activeTab === "coverage" ? (
                            <div className="space-y-4">
                                {filteredPress.map((item) => (
                                    <div key={item.id} className="group bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm flex flex-col md:flex-row items-center gap-6 hover:shadow-md transition-all">
                                        <div className="w-16 h-16 bg-gray-50 flex items-center justify-center p-2 border border-black/5 shrink-0">
                                            <img src={item.logo_path} alt={item.publication} className="max-w-full max-h-full object-contain grayscale opacity-60 group-hover:opacity-100 transition-all shadow-sm" />
                                        </div>
                                        <div className="flex-1 text-center md:text-left">
                                            <div className="flex items-center gap-3 justify-center md:justify-start text-xs font-bold tracking-widest text-black dark:text-white/60 uppercase mb-1">
                                                <span className="text-jazz-brown">{item.type}</span>
                                                <span>•</span>
                                                <span>{item.date_display}</span>
                                                <span className={`px-2 py-0.5 rounded text-[10px] border ${item.status === 'active' ? 'border-green-200 text-green-600' : 'border-red-200 text-red-600'}`}>
                                                    {item.status.toUpperCase()}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-black dark:text-white" style={{ fontFamily: "Crimson Pro, serif" }}>{item.headline}</h3>
                                            <p className="text-xs text-black dark:text-white/50 italic font-serif">{item.publication}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => openQuickView("press", item)}
                                                className="p-3 bg-gray-50 dark:bg-zinc-800 text-black dark:text-white/40 rounded-xl hover:bg-jazz-brown/10 hover:text-jazz-brown transition-all"
                                                title="Quick View"
                                            >
                                                <Maximize2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => togglePressStatus(item)}
                                                className={`p-3 bg-gray-50 dark:bg-zinc-800 rounded-xl transition-all ${item.status === 'active' ? 'text-green-500 hover:bg-green-50' : 'text-black dark:text-white/40 hover:bg-jazz-brown/10 hover:text-jazz-brown'}`}
                                                title={item.status === 'active' ? "Active - Click to Disable" : "Disabled - Click to Enable"}
                                            >
                                                <Power size={18} />
                                            </button>
                                            <button
                                                onClick={() => navigate(`/admin/press/edit/${item.id}`)}
                                                className="p-3 bg-gray-50 dark:bg-zinc-800 text-black dark:text-white/40 rounded-xl hover:bg-black hover:text-white transition-all"
                                                title="Edit"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => openDeleteModal("press", item)}
                                                className="p-3 bg-gray-50 text-red-400 rounded-xl hover:bg-red-500 hover:text-white transition-all"
                                                title="Delete"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {filteredPress.length === 0 && (
                                    <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-black/10">
                                        <Newspaper className="mx-auto text-black/10 mb-4" size={48} />
                                        <h3 className="text-lg font-medium text-black dark:text-white/50">No coverage items found</h3>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {filteredQuotes.map((quote) => (
                                    <div key={quote.id} className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm relative group overflow-hidden flex flex-col">
                                        <div className="absolute top-0 left-0 w-1.5 h-full bg-jazz-brown" />
                                        <QuoteIcon size={32} className="text-jazz-brown/10 absolute top-4 right-4" />

                                        <p className="text-lg text-black dark:text-white/80 font-serif italic leading-relaxed mb-6" style={{ fontFamily: "Crimson Pro, serif" }}>
                                            "{quote.text}"
                                        </p>

                                        <div className="flex items-center justify-between mt-auto pt-4">
                                            <div>
                                                <p className="text-xs font-bold text-black dark:text-white uppercase tracking-widest">— {quote.source}</p>
                                                <span className={`text-xs font-bold uppercase tracking-widest ${quote.status === 'active' ? 'text-green-500' : 'text-red-500'}`}>
                                                    {quote.status}
                                                </span>
                                            </div>

                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => openQuickView("quote", quote)}
                                                    className="p-2.5 bg-gray-50 dark:bg-zinc-800 text-black dark:text-white/40 rounded-lg hover:bg-jazz-brown/10 hover:text-jazz-brown transition-all"
                                                    title="Quick View"
                                                >
                                                    <Maximize2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => toggleQuoteStatus(quote)}
                                                    className={`p-2.5 bg-gray-50 dark:bg-zinc-800 rounded-lg transition-all ${quote.status === 'active' ? 'text-green-500 hover:bg-green-50' : 'text-black dark:text-white/40 hover:bg-jazz-brown/10 hover:text-jazz-brown'}`}
                                                    title={quote.status === 'active' ? "Active" : "Disabled"}
                                                >
                                                    <Power size={16} />
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/admin/press/quotes/edit/${quote.id}`)}
                                                    className="p-2.5 bg-gray-50 dark:bg-zinc-800 text-black dark:text-white/40 rounded-lg hover:bg-black hover:text-white transition-all"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => openDeleteModal("quote", quote)}
                                                    className="p-2.5 bg-gray-50 text-red-300 rounded-lg hover:bg-red-500 hover:text-white transition-all"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {filteredQuotes.length === 0 && (
                                    <div className="col-span-full py-20 text-center bg-white rounded-2xl border border-dashed border-black/10">
                                        <QuoteIcon className="mx-auto text-black/10 mb-4" size={48} />
                                        <h3 className="text-lg font-medium text-black dark:text-white/50">No quotes found</h3>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
                onConfirm={handleDelete}
                title={`Delete ${deleteModal.type === "press" ? "Coverage" : "Quote"}`}
                message={`Are you sure you want to delete "${deleteModal.title}"? This action cannot be undone.`}
            />

            <QuickViewModal
                isOpen={previewModal.isOpen}
                onClose={() => setPreviewModal({ isOpen: false, item: null, type: null })}
                item={previewModal.item}
                type={previewModal.type}
                onEdit={handleQuickViewEdit}
            />
        </>
    );
}
